import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    type ColumnDef,
    type RowData,
    type SortingState,
    type Row,
} from '@tanstack/react-table';
import { CopyableId, Button, ConfirmDialog } from '@/kit';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useApplications, useDeleteApplication } from '../../../hooks/useApplications';
import { handleApiError } from '../../../api/errorMapping';
import { ApiError } from '../../../api/errors';
import { Gate } from '../../../components/permissions/Gate';
import { CreateApplicationWizard } from './CreateApplicationWizard';
import { Badge } from '../../../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../components/ui/table';
import { DataTableToolbar } from '../../../components/shared/DataTableToolbar';
import { DataTablePagination } from '../../../components/shared/DataTablePagination';
import { SortableHeader } from '../../../components/shared/SortableHeader';
import { ContentLoading } from '../../../components/shared/ContentLoading';
import { globalFilterFn } from '../../../lib/dataTable';
import { cn } from '../../../lib/cn';
import type { ApplicationListItemLocal } from '../../../api/types/ApplicationLocal';

// Alineación por columna: el dato textual (Aplicación) va a la izquierda; los badges/contadores,
// centrados. La derivamos del `meta.align` de cada columna en el render del header y la celda,
// así header y contenido comparten alineación. Default 'center' (sin meta = centrado).
declare module '@tanstack/react-table' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface ColumnMeta<TData extends RowData, TValue> {
        align?: 'left' | 'center';
    }
}

/**
 * Aplicaciones a nivel PLATAFORMA (`/platform/applications`). Reemplaza la pantalla deprecada de
 * "Tipos de tenant": el modelo de tipos hardcodeados (retail/agents) lo sustituyó el modelo
 * data-driven de Aplicaciones (`GET /applications`). Misma composición que PlatformRolesTable /
 * TenantTypesTable (useReactTable + DataTableToolbar/Pagination + SortableHeader), sirviendo la lista
 * vía `useApplications()`. Gateada server-side por `platform.application.manage` (scope PLATFORM); el
 * mismo permiso gatea el item de menú.
 *
 * Regla anti-UUID: el dato principal es el `displayName` legible; el `applicationId` (slug) va como
 * subtítulo muted, nunca como columna principal.
 */
// DEC-RBAC-088: acción de borrado (hard delete) por fila. Encapsula estado del confirm + mutation
// para no depender del estado del contenedor desde las columnas memoizadas. El 409
// APPLICATION_HAS_TENANTS lo traduce a un mensaje claro con el conteo de tenants (viene en
// `error.data.tenants`); el resto de errores caen en el mapeo genérico `handleApiError`.
function DeleteApplicationAction({ application }: { application: ApplicationListItemLocal }) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const deleteMut = useDeleteApplication();
    const runDelete = useMutation({
        mutationFn: () => deleteMut.mutateAsync(application.applicationId),
        onSuccess: () => {
            toast.success('Aplicación eliminada');
            setConfirmOpen(false);
        },
        onError: (error: unknown) => {
            if (error instanceof ApiError && error.code === 'APPLICATION_HAS_TENANTS') {
                const rawCount = (error.data as { tenants?: number } | undefined)?.tenants;
                const cantidad = typeof rawCount === 'number' ? String(rawCount) : 'varios';
                toast.error(
                    `No se puede eliminar: la aplicación tiene ${cantidad} tenant(s) asociado(s). ` +
                        'Elimina o reasigna los tenants primero.',
                );
                setConfirmOpen(false);
                return;
            }
            handleApiError(error);
        },
    });

    return (
        <Gate permission="platform.application.manage" scope="PLATFORM">
            <Button
                variant="ghost"
                size="sm"
                leftIcon={<Trash2 className="h-4 w-4" />}
                aria-label={`Eliminar ${application.displayName}`}
                onClick={(e) => {
                    // La fila también navega; evitamos el doble disparo.
                    e.stopPropagation();
                    setConfirmOpen(true);
                }}
            >
                Eliminar
            </Button>
            <ConfirmDialog
                open={confirmOpen}
                onOpenChange={setConfirmOpen}
                title="Eliminar aplicación"
                description={
                    <>
                        Se eliminará la aplicación <strong>{application.displayName}</strong> (
                        {application.applicationId}). Esta acción no se puede deshacer. ¿Continuar?
                    </>
                }
                confirmLabel="Eliminar aplicación"
                destructive
                loading={runDelete.isPending}
                onConfirm={() => {
                    runDelete.mutate();
                }}
            />
        </Gate>
    );
}

export function ApplicationsTable() {
    const navigate = useNavigate();
    const [createOpen, setCreateOpen] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([{ id: 'displayName', desc: false }]);

    const { data, isLoading, isError } = useApplications();

    const goToDetail = (applicationId: string): void => {
        void navigate({
            to: '/platform/applications/$applicationId',
            params: { applicationId },
            search: { tab: 'levels' },
        });
    };

    const columns = useMemo<ColumnDef<ApplicationListItemLocal>[]>(
        () => [
            {
                accessorKey: 'displayName',
                meta: { align: 'center' },
                header: ({ column }) => <SortableHeader column={column} title="Aplicación" />,
                cell: (info) => (
                    <span className="flex flex-col items-center gap-0.5">
                        <span className="font-medium">{info.getValue() as string}</span>
                        <CopyableId label="ID" value={info.row.original.applicationId} />
                    </span>
                ),
            },
            {
                id: 'levels',
                accessorFn: (row) => row.levels.length,
                meta: { align: 'center' },
                header: ({ column }) => <SortableHeader column={column} title="Niveles" />,
                enableGlobalFilter: false,
                cell: (info) => (
                    <div className="flex flex-wrap justify-center gap-1">
                        {info.row.original.levels.map((lvl) => (
                            <Badge key={lvl.level} variant="outline">
                                {lvl.displayName}
                            </Badge>
                        ))}
                    </div>
                ),
            },
            {
                id: 'seedRoles',
                accessorFn: (row) => row.seedRoles.length,
                meta: { align: 'center' },
                header: ({ column }) => <SortableHeader column={column} title="# roles seed" />,
                enableGlobalFilter: false,
            },
            {
                id: 'status',
                accessorFn: (row) => row.status,
                meta: { align: 'center' },
                header: ({ column }) => <SortableHeader column={column} title="Estado" />,
                cell: (info) => {
                    const status = info.getValue() as ApplicationListItemLocal['status'];
                    return (
                        <Badge variant={status === 'ACTIVE' ? 'default' : 'secondary'}>
                            {status === 'ACTIVE' ? 'Activa' : 'Archivada'}
                        </Badge>
                    );
                },
            },
            {
                id: 'actions',
                meta: { align: 'center' },
                header: () => <span className="sr-only">Acciones</span>,
                enableGlobalFilter: false,
                enableSorting: false,
                cell: (info) => (
                    // Zona muerta de navegación: la fila entera navega al detalle (role="link"), pero
                    // cualquier click/tecla dentro de la celda de acciones (botones + el ConfirmDialog de
                    // borrado, inline) NO debe llevar a editar. Frenamos el bubbling acá, a nivel celda —
                    // así el borrado solo pregunta y borra, sin meterte a la edición.
                    <div
                        className="flex items-center justify-center gap-1"
                        role="presentation"
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        <Gate permission="platform.application.manage" scope="PLATFORM">
                            <Button
                                variant="ghost"
                                size="sm"
                                leftIcon={<Pencil className="h-4 w-4" />}
                                aria-label={`Editar ${info.row.original.displayName}`}
                                onClick={() => {
                                    goToDetail(info.row.original.applicationId);
                                }}
                            >
                                Editar
                            </Button>
                        </Gate>
                        <DeleteApplicationAction application={info.row.original} />
                    </div>
                ),
            },
        ],
        // goToDetail es estable (deriva de navigate); las columnas no dependen de otro estado.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const rows = useMemo(() => data ?? [], [data]);
    const table = useReactTable({
        data: rows,
        columns,
        state: { globalFilter, sorting },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 25 } },
    });

    if (isLoading) return <ContentLoading />;

    if (isError) {
        return (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                No se pudieron cargar las aplicaciones. Intenta de nuevo.
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Gate permission="platform.application.manage" scope="PLATFORM">
                    <Button
                        leftIcon={<Plus className="h-4 w-4" />}
                        onClick={() => {
                            setCreateOpen(true);
                        }}
                    >
                        Crear aplicación
                    </Button>
                </Gate>
            </div>

            <CreateApplicationWizard
                isOpen={createOpen}
                onClose={() => {
                    setCreateOpen(false);
                }}
            />

            <DataTableToolbar
                value={globalFilter}
                onChange={setGlobalFilter}
                placeholder="Buscar por nombre o id…"
            />

            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id}>
                            {hg.headers.map((h) => (
                                <TableHead
                                    key={h.id}
                                    className={cn(
                                        h.column.columnDef.meta?.align === 'left'
                                            ? 'text-left'
                                            : 'text-center',
                                    )}
                                >
                                    {flexRender(h.column.columnDef.header, h.getContext())}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.length === 0 ? (
                        <TableRow>
                            <TableCell
                                colSpan={columns.length}
                                className="h-24 text-center text-muted-foreground"
                            >
                                No hay aplicaciones.
                            </TableCell>
                        </TableRow>
                    ) : (
                        table.getRowModel().rows.map((r: Row<ApplicationListItemLocal>) => (
                            // La fila navega al detalle; el acceso de EDICIÓN lo gatea el `beforeLoad`
                            // de la ruta detalle (platform.application.manage): un user sin el permiso
                            // que clickea es redirigido a la lista (editor inalcanzable). Ver review #3-a E.
                            <TableRow
                                key={r.id}
                                role="link"
                                tabIndex={0}
                                aria-label={`Abrir ${r.original.displayName}`}
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => {
                                    goToDetail(r.original.applicationId);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        goToDetail(r.original.applicationId);
                                    }
                                }}
                            >
                                {r.getVisibleCells().map((c) => (
                                    <TableCell
                                        key={c.id}
                                        className={cn(
                                            'align-middle',
                                            c.column.columnDef.meta?.align === 'left'
                                                ? 'text-left'
                                                : 'text-center',
                                        )}
                                    >
                                        {flexRender(c.column.columnDef.cell, c.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>

            <DataTablePagination table={table} />
        </div>
    );
}
